import { CONTENT_TESTING_ROUTE_PATHS, getContentTestingFromPath, withFromPrice } from '../content-testing/routes';
import { translations } from '../i18n/translations';
import { getCounterpartPath, getLegalFromPath, LEGAL_ROUTE_PATHS, normalizePath } from '../legal/routes';
import homeGraph from './homeGraph.json';

export const SITE = 'https://www.tamerin.tech';

export const INDEX_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
export const NOINDEX_ROBOTS = 'noindex, nofollow, noarchive';

export const ENGLISH_TITLE = 'TamerinTECH - Machine Learns, You Grow | ContextFriend and MBot';
export const ENGLISH_DESCRIPTION =
  'TamerinTECH builds AI learning products for schools and families in Bavaria, Germany. ContextFriend is a private classroom memory that runs on the school\'s own hardware (supervised school pilots). MBot is our live AI math tutor for grades 5 and up. Open to technology cooperation with schools. Machine Learns, You Grow.';
export const ENGLISH_OG_TITLE = 'TamerinTECH - Machine Learns, You Grow';
export const ENGLISH_OG_DESCRIPTION =
  'AI learning products for classrooms and families. ContextFriend: a private classroom memory that stays in the school (supervised school pilots). MBot: our live AI math tutor for grades 5+.';

export const GERMAN_TITLE = 'TamerinTECH – Machine Learns, You Grow | ContextFriend und MBot';
export const GERMAN_DESCRIPTION =
  'TamerinTECH entwickelt KI-Lernprodukte für Schulen und Familien in Bayern. ContextFriend ist ein privates Klassengedächtnis auf der Hardware der Schule (begleitete Schulpiloten). MBot ist unser KI-Mathe-Tutor ab Klasse 5. Offen für Technologiekooperationen mit Schulen. Machine Learns, You Grow.';
export const GERMAN_OG_TITLE = 'TamerinTECH – Machine Learns, You Grow';
export const GERMAN_OG_DESCRIPTION =
  'KI-Lernprodukte für Klassenzimmer und Familien. ContextFriend: ein privates Klassengedächtnis, das in der Schule bleibt (begleitete Schulpiloten). MBot: unser KI-Mathe-Tutor ab Klasse 5.';

const LEGAL_DESCRIPTION: Record<'en' | 'de', Record<'privacy' | 'impressum' | 'cookies', string>> = {
  en: {
    privacy:
      'Privacy policy of TamerinTECH GmbH: how the website, chatbot and products handle personal data under the GDPR.',
    impressum:
      'Legal notice of TamerinTECH GmbH in Schliersee, Germany: company register, contact and the responsible person.',
    cookies:
      'Cookie policy of TamerinTECH GmbH: which cookies this website uses and how to control them.',
  },
  de: {
    privacy:
      'Datenschutzerklärung der TamerinTECH GmbH: wie Website, Chatbot und Produkte personenbezogene Daten nach der DSGVO verarbeiten.',
    impressum:
      'Impressum der TamerinTECH GmbH in Schliersee: Registerangaben, Kontakt und die verantwortliche Person.',
    cookies:
      'Cookie-Richtlinie der TamerinTECH GmbH: welche Cookies diese Website verwendet und wie Sie sie steuern.',
  },
};

export type HreflangLink = { hreflang: string; href: string };

export type PageSeo = {
  title: string;
  description: string;
  canonical: string;
  lang: 'en' | 'de';
  robots: string;
  ogTitle: string;
  ogDescription: string;
  ogLocale: 'en_US' | 'de_DE';
  ogLocaleAlternate: 'en_US' | 'de_DE';
  hreflang: HreflangLink[];
  jsonLd: Record<string, unknown> | null;
};

type GraphNode = Record<string, unknown> & { '@type'?: string };
type Graph = { '@context': string; '@graph': GraphNode[] };

export const HOME_HREFLANG: HreflangLink[] = [
  { hreflang: 'en', href: `${SITE}/` },
  { hreflang: 'de', href: `${SITE}/de` },
  { hreflang: 'x-default', href: `${SITE}/` },
];

export const PRERENDER_ROUTES: string[] = [
  '/',
  '/de',
  ...LEGAL_ROUTE_PATHS,
  ...CONTENT_TESTING_ROUTE_PATHS,
  '/drill-1',
  '/drill-1/ergebnisse',
];

function absolute(path: string): string {
  return path === '/' ? `${SITE}/` : `${SITE}${path}`;
}

export function germanHomeGraph(): Graph {
  const graph = JSON.parse(JSON.stringify(homeGraph)) as Graph;
  const faq = graph['@graph'].find((node) => node['@type'] === 'FAQPage');
  if (!faq) {
    throw new Error('English homepage graph is missing FAQPage');
  }
  faq.mainEntity = translations.de.faq.items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  }));

  const website = graph['@graph'].find((node) => node['@type'] === 'WebSite');
  if (website) {
    website.url = `${SITE}/de`;
    website.inLanguage = 'de';
  }

  graph['@graph'].push({
    '@type': 'WebPage',
    '@id': `${SITE}/de#webpage`,
    url: `${SITE}/de`,
    name: GERMAN_TITLE,
    description: GERMAN_DESCRIPTION,
    inLanguage: 'de',
    isPartOf: { '@id': `${SITE}/#website` },
  });

  return graph;
}

function homeSeo(lang: 'en' | 'de'): PageSeo {
  const german = lang === 'de';
  return {
    title: german ? GERMAN_TITLE : ENGLISH_TITLE,
    description: german ? GERMAN_DESCRIPTION : ENGLISH_DESCRIPTION,
    canonical: german ? `${SITE}/de` : `${SITE}/`,
    lang,
    robots: INDEX_ROBOTS,
    ogTitle: german ? GERMAN_OG_TITLE : ENGLISH_OG_TITLE,
    ogDescription: german ? GERMAN_OG_DESCRIPTION : ENGLISH_OG_DESCRIPTION,
    ogLocale: german ? 'de_DE' : 'en_US',
    ogLocaleAlternate: german ? 'en_US' : 'de_DE',
    hreflang: HOME_HREFLANG,
    jsonLd: (german ? germanHomeGraph() : homeGraph) as unknown as Record<string, unknown>,
  };
}

function webPage(name: string, description: string, url: string, lang: 'en' | 'de'): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url,
    inLanguage: lang,
    isPartOf: { '@id': `${SITE}/#website` },
    publisher: { '@id': `${SITE}/#organization` },
  };
}

function withSocial(
  seo: Omit<PageSeo, 'ogTitle' | 'ogDescription' | 'ogLocale' | 'ogLocaleAlternate'> & {
    ogTitle?: string;
    ogDescription?: string;
  }
): PageSeo {
  return {
    ...seo,
    ogTitle: seo.ogTitle ?? seo.title,
    ogDescription: seo.ogDescription ?? seo.description,
    ogLocale: seo.lang === 'de' ? 'de_DE' : 'en_US',
    ogLocaleAlternate: seo.lang === 'de' ? 'en_US' : 'de_DE',
  };
}

export function getPageSeo(pathname: string): PageSeo {
  const path = normalizePath(pathname);

  if (path === '/') return homeSeo('en');
  if (path === '/de') return homeSeo('de');

  const legal = getLegalFromPath(path);
  if (legal) {
    const title = `${translations[legal.language].legal[legal.type].title} | TamerinTECH`;
    const description = LEGAL_DESCRIPTION[legal.language][legal.type];
    const canonical = absolute(path);
    const counterpart = getCounterpartPath(path);
    const englishPath = legal.language === 'en' ? path : counterpart;
    const germanPath = legal.language === 'de' ? path : counterpart;
    return withSocial({
      title,
      description,
      canonical,
      lang: legal.language,
      robots: INDEX_ROBOTS,
      hreflang:
        englishPath && germanPath
          ? [
              { hreflang: 'en', href: absolute(englishPath) },
              { hreflang: 'de', href: absolute(germanPath) },
              { hreflang: 'x-default', href: absolute(englishPath) },
            ]
          : [],
      jsonLd: webPage(title, description, canonical, legal.language),
    });
  }

  const scan = getContentTestingFromPath(path);
  if (scan) {
    const copy = translations[scan.language].contentTesting;
    const description = withFromPrice(copy.metaDescription, scan.currency);
    return withSocial({
      title: copy.metaTitle,
      description,
      canonical: absolute(scan.path),
      lang: scan.language,
      robots: NOINDEX_ROBOTS,
      hreflang: [],
      jsonLd: null,
    });
  }

  if (path === '/drill-1' || path.startsWith('/drill-1/')) {
    const results = path.startsWith('/drill-1/ergebnisse');
    const canonical = absolute(results ? '/drill-1/ergebnisse' : '/drill-1');
    const title = results ? 'Drill-1 Ergebnisse | TamerinTECH' : 'Interne Phishing-Übung | TamerinTECH';
    return withSocial({
      title,
      description: 'Interne Seite der TamerinTECH GmbH. Nicht zur Indexierung vorgesehen.',
      canonical,
      lang: 'de',
      robots: NOINDEX_ROBOTS,
      hreflang: [],
      jsonLd: null,
    });
  }

  return withSocial({
    title: 'Page not found | TamerinTECH',
    description: 'This address is not a page on the TamerinTECH website.',
    canonical: '',
    lang: 'en',
    robots: NOINDEX_ROBOTS,
    hreflang: [],
    jsonLd: null,
  });
}
